import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress } from '@material-ui/core';
import { ArrowBack, GetApp, EmojiEvents } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { Switch, Route, useHistory, useParams } from 'react-router-dom';
import { TournamentType } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import {
  TournamentData, TournamentQuery, TournamentVars, TournamentParticipantsData, TournamentParticipantsQuery, TournamentTeamsData, TournamentTeamsQuery,
  DungeonClearStatsData, DungeonClearStatsQuery, TournamentMatchesData, TournamentMatchesQuery, TournamentRoundsData, TournamentRoundsQuery,
} from '@pvplevelup/core/dist/queries/tournament/tournament';
import { PageEmpty, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { downloadApp } from 'utilities/download';
import { TournamentList } from './TournamentList';
import { TournamentView } from './TournamentView';
import { MatchDetail } from './MatchDetail';
import { TOURNAMENT_ACCENT } from './tournamentFormat';

const useStyles = makeStyles((theme) => ({
  appCard: {
    padding: theme.spacing(2, 2.25),
    borderRadius: 14,
    background: `radial-gradient(circle at 100% 0%, rgba(255, 179, 0, 0.18), transparent 55%), ${PAGE_KIT.panel}`,
    border: '1px solid rgba(255, 179, 0, 0.3)',
    boxShadow: PAGE_KIT.shadow,
  },
  appIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    color: '#1a1300',
    background: `linear-gradient(135deg, #ffe082, ${TOURNAMENT_ACCENT})`,
    marginBottom: 10,
  },
  appTitle: {
    fontWeight: 900,
    fontSize: '1.02rem',
  },
  appText: {
    marginTop: 4,
    fontSize: '0.86rem',
    lineHeight: 1.5,
    color: 'rgba(255, 255, 255, 0.75)',
  },
}));

/** Tournaments on the website: read only. Signing up and organizing happen in the app. */
export const TournamentsPage: React.FC = () => (
  <div className="content-container">
    <TournamentList basePath="/tournaments" />
  </div>
);

/** The app is where you sign up: the website points there. */
const GetTheApp: React.FC = () => {
  const classes = useStyles();
  const page = usePageStyles();
  return (
    <div className={classes.appCard}>
      <div className={classes.appIcon}><EmojiEvents /></div>
      <div className={classes.appTitle}>Sign up in the app</div>
      <div className={classes.appText}>
        Tournaments run through the PvP Levelup app: it signs you up, readies you up and records every match.
      </div>
      <Button className={page.primaryButton} style={{width: '100%', marginTop: 14}} onClick={downloadApp}>
        <GetApp style={{fontSize: 18, marginRight: 6}} />Download the app
      </Button>
    </div>
  );
}

export const TournamentPage: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const page = usePageStyles();
  const history = useHistory();
  const tournament = useQuery<TournamentData, TournamentVars>(TournamentQuery, {variables: {id}, fetchPolicy: 'cache-and-network', pollInterval: 30000});
  const participants = useQuery<TournamentParticipantsData, TournamentVars>(TournamentParticipantsQuery, {variables: {id}, fetchPolicy: 'cache-and-network', pollInterval: 30000});
  const teams = useQuery<TournamentTeamsData, TournamentVars>(TournamentTeamsQuery, {variables: {id}, fetchPolicy: 'cache-and-network', pollInterval: 30000});
  const t = tournament.data?.tournament;
  const stats = useQuery<DungeonClearStatsData>(DungeonClearStatsQuery, {skip: t?.type != TournamentType.PvE});
  const matches = useQuery<TournamentMatchesData, TournamentVars>(TournamentMatchesQuery, {variables: {id}, skip: !t?.stage || t.stage == 'Cup', fetchPolicy: 'cache-and-network', pollInterval: 30000});
  const rounds = useQuery<TournamentRoundsData, TournamentVars>(TournamentRoundsQuery, {variables: {id}, skip: t?.stage != 'Cup', fetchPolicy: 'cache-and-network', pollInterval: 30000});
  const [openMatchId, setOpenMatchId] = useState<string | null>(null);

  if(tournament.loading && !t)
    return <div className="content-container" style={{display: 'grid', placeItems: 'center', minHeight: 300}}><CircularProgress /></div>;
  if(!t)
    return (
      <div className="content-container">
        <div className={page.panel}>
          <PageEmpty title="Tournament not found" text="It may have been removed, or the link is wrong."
            action={<Button className={page.secondaryButton} onClick={() => history.push('/tournaments')}>All tournaments</Button>} />
        </div>
      </div>
    );

  return (
    <div className="content-container">
      <Button onClick={() => history.push('/tournaments')} style={{marginBottom: 10, textTransform: 'none', color: 'rgba(255, 255, 255, 0.7)'}}>
        <ArrowBack style={{fontSize: 18, marginRight: 6}} />All tournaments
      </Button>
      <TournamentView tournament={t} participants={participants.data?.tournamentParticipants || []} teams={teams.data?.tournamentTeams || []}
        dungeonStats={stats.data?.dungeonClearStats} matches={matches.data?.tournamentMatches} rounds={rounds.data?.tournamentRounds} side={<GetTheApp />}
        onMatchClick={(match) => setOpenMatchId(String(match._id))} />
      <MatchDetail tournament={t} matches={matches.data?.tournamentMatches || []} onClose={() => setOpenMatchId(null)} userPath="/user"
        match={(matches.data?.tournamentMatches || []).find((x) => String(x._id) == openMatchId) || null} />
    </div>
  );
}

export default function TournamentContainer(){
  return (
    <Switch>
      <Route exact path="/tournaments" component={TournamentsPage} />
      <Route path="/tournaments/:id" component={TournamentPage} />
    </Switch>
  );
}
